import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import {
  Drawer,
  Field,
  Icon,
  Layout,
  LogoLoader,
  Tag,
  Text,
  TextField,
  Banner,
} from '@loadsmart/miranda-react'

import { RequestStatus } from 'core/domain/Request'

import { useCreateOpenDockCarrier } from '../hooks/useCreateOpenDockCarrier'
import { useOpenDockDrawer } from './OpenDockDrawerContext'

export function CreateOpenDockAccount({
  carrierStatus,
  onCarrierCreated,
}: {
  carrierStatus: RequestStatus
  onCarrierCreated: (carrierId: string) => void
}) {
  const { shipmentCarrier, shipmentUUID, carrierId, setStep } = useOpenDockDrawer()

  const isSignedUp = Boolean(carrierId)

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')

  const { create, requestStatus } = useCreateOpenDockCarrier()

  const isPending = requestStatus === RequestStatus.Pending
  const canSubmit = firstName.trim() !== '' && lastName.trim() !== '' && !isPending

  const isVerifyingCarrier =
    carrierStatus === RequestStatus.Initial || carrierStatus === RequestStatus.Pending

  useEffect(() => {
    if (isSignedUp) {
      setStep('instructions')
    }
  }, [isSignedUp, setStep])

  async function handleContinue() {
    const carrier = await create({ shipmentUUID, firstName, lastName })

    if (!carrier) {
      toast.error('Could not create your Opendock account, try again later')
      return
    }

    onCarrierCreated(carrier.id)
    setStep('instructions')
  }

  if (isVerifyingCarrier || isSignedUp) {
    return (
      <>
        <Drawer.Body>
          <Layout.Stack align="center" gap="spacing-4" paddingT="spacing-16">
            <LogoLoader />
            <Text color="color-text-secondary">Verifying your Opendock account</Text>
          </Layout.Stack>
        </Drawer.Body>
        <Drawer.Actions>
          <Drawer.ActionPrimary disabled loading>
            Create account and continue
          </Drawer.ActionPrimary>
        </Drawer.Actions>
      </>
    )
  }

  return (
    <>
      <Drawer.Body>
        <Layout.Stack gap="spacing-6">
          <Banner variant="warning">
            <Banner.Icon>
              <Icon name="user" color="color-warning-100" />
            </Banner.Icon>
            <Banner.Title>Create your Opendock account</Banner.Title>
            <Banner.Description>
              Facilities will have your carrier info ready on arrival, and your actions will be
              logged under your name
            </Banner.Description>
          </Banner>

          <Layout.Stack gap="spacing-1">
            <Text variant="body-sm" color="color-text-tertiary">
              Carrier
            </Text>
            <Text variant="body-md-bold">{shipmentCarrier.name}</Text>
            <Layout.Group gap="spacing-1">
              {shipmentCarrier.mc && (
                <Tag variant="neutral" size="small">
                  MC {shipmentCarrier.mc}
                </Tag>
              )}
              {shipmentCarrier.dot && (
                <Tag variant="neutral" size="small">
                  DOT {shipmentCarrier.dot}
                </Tag>
              )}
            </Layout.Group>
          </Layout.Stack>

          <Layout.Grid gap="spacing-4">
            <Field required>
              <Field.Label>First name</Field.Label>
              <TextField
                value={firstName}
                placeholder="First name"
                data-testid="first-name-field"
                onInput={event => setFirstName((event.target as HTMLInputElement).value)}
              />
            </Field>
            <Field required>
              <Field.Label>Last name</Field.Label>
              <TextField
                value={lastName}
                placeholder="Last name"
                data-testid="last-name-field"
                onInput={event => setLastName((event.target as HTMLInputElement).value)}
              />
            </Field>
          </Layout.Grid>

          <Layout.Stack gap="spacing-1">
            <Field>
              <Field.Label>Email</Field.Label>
              <TextField disabled value={shipmentCarrier.email} />
            </Field>
            <Text variant="body-sm" color="color-text-tertiary">
              The Opendock account will be created for the account you are currently logged into on
              ShipperGuide
            </Text>
          </Layout.Stack>
        </Layout.Stack>
      </Drawer.Body>
      <Drawer.Actions>
        <Drawer.ActionPrimary onClick={handleContinue} disabled={!canSubmit} loading={isPending}>
          Create account and continue
        </Drawer.ActionPrimary>
      </Drawer.Actions>
    </>
  )
}
